import { Category, Meta, fetchKidsContent } from 'common/apiService';
import ContentList from 'common/ContentList';
import Layout from 'common/Layout/Layout';
import { Icon, Text } from 'common/styles';
import usePagination from 'common/usePagination';
import WidgetManager from 'common/Components/Widgets/WidgetManager';
import InfiniteScroll from 'common/InfiniteScroll';
import { __t } from 'common/i18n';
import ContentListLoading from 'common/ContentListLoading';
import { ThemeProvider } from '@emotion/react';
import paletteLight from 'common/theme/paletteLight';
import { IconKids } from 'common/icons';
import xIPCountry from 'common/apiService/xIPCountry';
import * as Sentry from '@sentry/nextjs';
import Seo, { SITE_URL } from 'common/Seo';
import Breadcrumb from 'common/Breadcrumb';
import Head from 'next/head';
import { safeJsonLd } from 'common/utils/safeJsonLd';
import CompactHeader, { canShowCompactHeader } from 'common/Components/CompactHeader';

type KidsPageProps = {
  data: Category;
  initialUrl: string;
  meta: Meta;
};

export default function KidsPage(props: KidsPageProps) {
  const {
    data: { title, body, widgets, videos, compact_header, show_header_details },
    initialUrl,
    meta,
  } = props;

  const initialData = {
    data: videos || [],
    meta,
  };
  const { data: contents, getInfiniteScrollProps, hasMore } = usePagination({ initialUrl, initialData });

  const pageTitle = title || __t('common.kids');
  const description = `تماشای آنلاین فیلم و سریال‌های ${pageTitle} در فیلمنت`;

  const compact = compact_header && canShowCompactHeader(widgets);

  const breadcrumbs = [
    { name: __t('common.home'), url: '/' },
    { name: pageTitle, url: '/kids' },
  ];

  return (
    <ThemeProvider theme={{ palette: paletteLight }}>
      <Layout disableFooter={hasMore}>
        <Seo title={`${pageTitle} | ${__t('static_siteName')}`} description={description} path="/kids" />
        <Breadcrumb items={breadcrumbs} />
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: safeJsonLd({
                '@context': 'https://schema.org',
                '@type': 'CollectionPage',
                name: `${pageTitle} | ${__t('static_siteName')}`,
                description,
                url: `${SITE_URL}/kids`,
                isPartOf: { '@type': 'WebSite', '@id': `${SITE_URL}/#website` },
              }),
            }}
          />
        </Head>
        {show_header_details &&
          (compact ? (
            <CompactHeader title={pageTitle} body={body} icon={<IconKids />} />
          ) : (
            <Text variant="h1" my={3}>
              <Icon ml={1}>
                <IconKids />
              </Icon>
              {pageTitle}
            </Text>
          ))}
        {!!widgets?.length && <WidgetManager widgets={widgets} headerInfo={{ compact }} />}
        {!!widgets?.length && (
          <Text variant="h2" weight="medium" mb={1} mt={7}>
            همه محتواها
          </Text>
        )}
        <InfiniteScroll {...getInfiniteScrollProps()} loader={<ContentListLoading />}>
          <ContentList items={contents} />
        </InfiniteScroll>
      </Layout>
    </ThemeProvider>
  );
}

export async function getServerSideProps({ req, res }) {
  xIPCountry({ req, res });
  try {
    const { data, meta } = await fetchKidsContent();
    return {
      props: {
        meta,
        data,
        initialUrl: meta.videos_next_url || '',
      },
    };
  } catch (err) {
    Sentry.withScope((scope) => {
      scope.setTag('Status', err.response?.status);
      scope.setTag('StatusText', err.response?.statusText);
      scope.setTag('Url', err.response?.config?.url);
      Sentry.captureException(err);
    });

    return { notFound: true };
  }
}
